import { useCallback, useMemo } from 'react'

import { EDITOR_REQUIRED_ANCHORS } from '@editor/config/constants'
import { CanvasConfigV2, CanvasEventsExtra } from '@editor/interfaces/editor'
import { CuboidFrameProps, PointArray } from '@editor/interfaces/interfaces'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import {
  addWorkingMaskPoint,
  resetWorking,
  setMouseAnchor,
  setWorkingMask,
} from '@editor/pages/projects/editor/tools/MaskPCD/store'
import { RootState, useAppDispatch } from '@editor/store/app'
import { selectCurrentAnchorPositionByTool } from '@editor/store/selectors'
import { useSelector } from 'react-redux'

// Selector for current anchor position specific to MaskPCD tool, for re-rendering optimization
const selectCurrentAnchorPosition = selectCurrentAnchorPositionByTool(BlueprintEditorTools.MaskPCD)

/**
 * Main canvas hook for PCD Trim tool.
 * Contains the logic for adding a cuboid (and the points required for it).
 *
 * The behavior is as follows:
 * - If the user clicks on the canvas, a new point is added to the cuboid.
 * - Or if the user triple clicks on the canvas, a new cuboid is created with the clicked point as the center.
 * - If the user has added 4 points, the cuboid is created.
 *
 * @param props
 */
export default function useMainCanvas(): CanvasConfigV2 {
  // Hooks
  const { getIntersection } = usePointSelection()

  // Store
  const dispatch = useAppDispatch()
  const currentAnchorPosition = useSelector(selectCurrentAnchorPosition)
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)
  const pcdBoundingSphereRadius = useSelector((state: RootState) => state.blueprint.editor.pcdBoundingSphereRadius)
  const workingMaskCuboid = useSelector((state: RootState) => state.maskPCD.workingMaskCuboid)

  // Vars
  const isToolSelected = useMemo(() => selectedTool === BlueprintEditorTools.MaskPCD, [selectedTool])

  /**
   * Add a new point for a cuboid.
   *
   * @param addedPoint New point to be added
   */
  const addCuboidAnchor = (point: PointArray) => {
    if (workingMaskCuboid && workingMaskCuboid.points.length >= EDITOR_REQUIRED_ANCHORS.cuboid) {
      return
    }

    dispatch(setMouseAnchor(undefined))
    dispatch(addWorkingMaskPoint(point))
  }
  return {
    objects: {
      circleAnchors: useMemo(() => {
        if (!isToolSelected) return []

        const anchors = []
        if (workingMaskCuboid?.points && workingMaskCuboid.points.length < EDITOR_REQUIRED_ANCHORS.cuboid) {
          anchors.push(
            ...workingMaskCuboid.points.map((point) => ({
              id: `cuboid-anchor-${point.join('-')}`,
              point,
              color: '#777',
            })),
          )
        }

        if (currentAnchorPosition) {
          anchors.push({ id: `maskregion-mouse-anchor`, point: currentAnchorPosition, color: '#777' })
        }

        return anchors
      }, [workingMaskCuboid, currentAnchorPosition, isToolSelected]),
      cuboidAnchorFrames: useMemo(() => {
        if (!workingMaskCuboid?.points || workingMaskCuboid.points.length >= EDITOR_REQUIRED_ANCHORS.cuboid) return []

        return [
          {
            id: `maskregion-cuboid-anchor-frames`,
            points: workingMaskCuboid.points,
            mousePoint: currentAnchorPosition,
          },
        ]
      }, [workingMaskCuboid, currentAnchorPosition]),
      cuboidFrames: useMemo((): CuboidFrameProps[] => {
        if (
          !workingMaskCuboid?.points ||
          workingMaskCuboid.points.length < EDITOR_REQUIRED_ANCHORS.cuboid ||
          !isToolSelected
        )
          return []

        return [
          {
            id: `maskregion-cuboid`,
            cuboidAnchor: {
              points: workingMaskCuboid.points,
              diameter: 0,
            },
            baseDiameter: 0, // Diameter is not used in this context, but required
            maxSize: pcdBoundingSphereRadius * 2,
            onChange: ({ center, extent, rotation }) => {
              dispatch(setWorkingMask({ region_id: `maskregion-cuboid`, center, rotation, extent }))
            },
          },
        ]
      }, [workingMaskCuboid, pcdBoundingSphereRadius, isToolSelected, dispatch]),
    },
    events: {
      onMouseUp: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, { isDragged }: CanvasEventsExtra) => {
        if (!isToolSelected || isDragged) return

        const point = getIntersection(e)
        if (!point) return

        addCuboidAnchor(point)
      },
      onCancel: useCallback(() => {
        if (!isToolSelected) return

        dispatch(resetWorking())
      }, [isToolSelected, dispatch]),
    },
  }
}
